import * as R from 'ramda'
import { of } from 'rxjs'
import { delayWhen, filter, map, pairwise, startWith, switchMap } from 'rxjs/operators'
import { ofType } from 'redux-observable'
import { LOCATION_CHANGE } from 'connected-react-router'
import * as RE from 'helpers/ramda-ext'
import { filtersCall } from 'api/planet/filters'
import { DATE_FILTERS_FORMAT } from 'constants/dateFormats'
import { isCatalogOrUniversePageAction } from 'domain/router'
import {
  getDepartureCitiesFromTransportation,
  getDepartureDatesFromOffset,
  getFiltersFromQuery,
  pathnameHasNotChanged
} from 'domain/filters'
import { awaitForSaleFetched } from 'store/epics/helpers'
import * as Actions from 'store/actions/index.ts'
import {
  getBeginDate,
  getCities,
  getCountryCode,
  getCultureCode,
  getIsSaleFetched,
  getMarketId,
  getOperationCode,
  getSaleKey,
  getSelectedDepartureDatesOffsetsRangeListFromQuery,
  getSelectedTransportationFromQuery,
  getShouldDoubleThePriceFeatureEnabled
} from 'store/selectors'

const transformFiltersStringInArray = R.pipe(
  R.map(getFiltersFromQuery),
  R.last,
  R.map(R.split(','))
)

const hasFilters = R.pipe(
  R.map(getFiltersFromQuery),
  R.either(
    R.pipe(R.head, RE.isNotEmpty),
    R.pipe(R.last, RE.isNotEmpty)
  )
)

const shouldFetchFilters = R.allPass([
  pathnameHasNotChanged,
  a => isCatalogOrUniversePageAction(a[1]),
  hasFilters
])

const fetchFiltersEpic = (actions$, state$) => actions$.pipe(
  ofType(LOCATION_CHANGE),
  pairwise(),
  filter(shouldFetchFilters),
  map(transformFiltersStringInArray),
  delayWhen(() => {
    const resourceFetched$ = actions$.pipe(ofType(Actions.resources.RESOURCE_FETCHED))

    return (getIsSaleFetched(state$.value))
      ? of({})
      : awaitForSaleFetched(resourceFetched$, state$)
  }),
  switchMap(action => {
    const state = state$.value

    if (R.isEmpty(action)) {
      return of(Actions.filters.onResetFilters())
    }

    const countryCode = getCountryCode(state)
    const cultureCode = getCultureCode(state)
    const marketId = getMarketId(state)
    const saleKey = getSaleKey(state)
    const beginDate = getBeginDate(state)
    const operationCode = getOperationCode(state)
    const departureDatesRanges = getSelectedDepartureDatesOffsetsRangeListFromQuery(state)
    const isResourceV2 = getShouldDoubleThePriceFeatureEnabled(state)
    const cities = getCities(state)
    const transportation = getSelectedTransportationFromQuery(state)

    const formattedDepartureDateOffset = action.departureDates && getDepartureDatesFromOffset(beginDate, DATE_FILTERS_FORMAT)(departureDatesRanges)

    return filtersCall(
      countryCode,
      cultureCode,
      getDepartureCitiesFromTransportation(cities, action.departureCities, transportation),
      formattedDepartureDateOffset,
      action.durations,
      marketId,
      operationCode,
      saleKey,
      isResourceV2
    ).pipe(
      switchMap(({ response: { products } }) => (
        of(Actions.filters.onFiltersFetched(products))
      )),
      startWith(Actions.filters.onFiltersFetchStarted())
    )
  })
)

export default fetchFiltersEpic
